import React, { useMemo }          from 'react';
import * as Yup                    from 'yup';
import { Formik, FormikConfig }    from 'formik';
import {
  IIntranetUser,
  IIntranetUserValues,
}                                  from '@typings/manageIntranetUsers';
import {
  EUserStatus,
  EUserRole,
}                                  from '@enums/common';
import { IAuthUser }               from '@modules/auth/authReducer';
import { IntranetUserDetailsForm } from '../components/IntranetUserDetailsForm';

const validationSchema: Yup.Schema<IIntranetUserValues> = Yup.object().shape({
  firstName       : Yup.string().required(),
  lastName        : Yup.string().required(),
  email           : Yup.string().email().required(),
  password        : Yup.string().min(8).max(32),
  status          : Yup.number().required(),
  confirmPassword : Yup.string().min(8).max(32)
    .when('password', {
      is   : (value) => !!value,
      then : Yup.string()
        .min(8)
        .max(32)
        .oneOf([Yup.ref('password')])
        .required(),
    }),
});

const getInitialValues = (intranetUser: IIntranetUser | null): IIntranetUserValues => ({
  id              : intranetUser?.id,
  firstName       : intranetUser?.firstName ?? '',
  lastName        : intranetUser?.lastName ?? '',
  email           : intranetUser?.email ?? '',
  password        : '',
  confirmPassword : '',
  status          : intranetUser?.status ?? EUserStatus.Active,
});

interface IProps {
  onSubmit            : FormikConfig<IIntranetUserValues>['onSubmit'];
  onCancel            : () => void;
  onDelete            : () => void;
  authUser            : IAuthUser;
  isUserDeleting      : boolean;
  currentIntranetUser : IIntranetUser | null;
}

export const IntranetUserDetailsPage: React.FC<IProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  authUser,
  isUserDeleting,
  currentIntranetUser,
}) => {
  const [isNewUser, isSuperAdmin] = useMemo<boolean[]>(
    () => ([
      !currentIntranetUser?.id,
      !!currentIntranetUser && currentIntranetUser.roles.includes(EUserRole.SuperAdmin),
    ]),
    [currentIntranetUser],
  );

  const [isEditingOwnAccount, isAuthRoleSuperAdmin] = useMemo<boolean[]>(
    () => ([
      authUser?.id === currentIntranetUser?.id,
      authUser?.role === EUserRole.SuperAdmin,
    ]),
    [authUser, currentIntranetUser],
  );

  const initialValues = useMemo<IIntranetUserValues>(
    () => getInitialValues(currentIntranetUser),
    [currentIntranetUser],
  );

  return (
    <Formik
      onSubmit         = {onSubmit}
      initialValues    = {initialValues}
      validationSchema = {validationSchema}
      enableReinitialize
    >
      <IntranetUserDetailsForm
        onCancel             = {onCancel}
        onDelete             = {onDelete}
        isNewUser            = {isNewUser}
        isSuperAdmin         = {isSuperAdmin}
        isEditingOwnAccount  = {isEditingOwnAccount}
        isAuthRoleSuperAdmin = {isAuthRoleSuperAdmin}
        isUserDeleting       = {isUserDeleting}
      />
    </Formik>
  );
};
